// src/pages/.../ManageMembers/hooks/useManageMembers.js

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  deleteAdherent,
  getAllAdherentsByClub,
} from "../../../../services/adherentServices";

import {
  getClubById,
  getClubByUser,
} from "../../../../services/clubServices";

import { getDojosByClub } from "../../../../services/dojoServices";
import { getGroupsByClub } from "../../../../services/groupServices";

import { isDemoUser } from "../../../../utils/isDemoUser";

import { readStoredUserInfo } from "../utils/manageMembers.utils";

export default function useManageMembers(addNotification) {
  const userInfo = useMemo(() => readStoredUserInfo(), []);

  const userId = userInfo?.userId ?? userInfo?.id;
  const roleId = Number(userInfo?.role_id || 0);
  const demoMode = isDemoUser(userInfo);

  const [club, setClub] = useState(null);
  const [adherents, setAdherents] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [groups, setGroups] = useState([]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const clubId = club?.id || null;

  const loadPageData = useCallback(async () => {
    if (!userId) {
      setClub(null);
      setAdherents([]);
      setDojos([]);
      setGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const clubData = demoMode
        ? await getClubById(2)
        : await getClubByUser(userId);

      if (!clubData?.id) {
        setClub(null);
        setAdherents([]);
        setDojos([]);
        setGroups([]);
        return;
      }

      setClub(clubData);

      const [
        adherentsResult,
        dojosResult,
        groupsResult,
      ] = await Promise.allSettled([
        getAllAdherentsByClub(clubData.id),
        getDojosByClub(clubData.id),
        getGroupsByClub(clubData.id),
      ]);

      if (adherentsResult.status === "fulfilled") {
        setAdherents(
          Array.isArray(adherentsResult.value)
            ? adherentsResult.value
            : [],
        );
      } else {
        console.error(
          "Erreur de chargement des adhérents :",
          adherentsResult.reason,
        );

        setAdherents([]);
      }

      if (dojosResult.status === "fulfilled") {
        setDojos(
          Array.isArray(dojosResult.value)
            ? dojosResult.value
            : [],
        );
      } else {
        console.error(
          "Erreur de chargement des lieux :",
          dojosResult.reason,
        );

        setDojos([]);
      }

      if (groupsResult.status === "fulfilled") {
        setGroups(
          Array.isArray(groupsResult.value)
            ? groupsResult.value
            : [],
        );
      } else {
        console.error(
          "Erreur de chargement des groupes :",
          groupsResult.reason,
        );

        setGroups([]);
      }

      const hasRejectedRequest = [
        adherentsResult,
        dojosResult,
        groupsResult,
      ].some((result) => result.status === "rejected");

      if (hasRejectedRequest) {
        addNotification(
          "Certaines données n'ont pas pu être chargées.",
          "warning",
        );
      }
    } catch (error) {
      console.error(
        "Erreur lors du chargement de la page adhérents :",
        error,
      );

      setClub(null);
      setAdherents([]);
      setDojos([]);
      setGroups([]);

      addNotification(
        "Erreur lors du chargement du club.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [addNotification, demoMode, userId]);

  const refreshAdherents = useCallback(async () => {
    if (!clubId) return;

    try {
      const data = await getAllAdherentsByClub(clubId);

      setAdherents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(
        "Erreur lors de l'actualisation des adhérents :",
        error,
      );

      addNotification(
        "Erreur lors de l'actualisation des adhérents.",
        "error",
      );
    }
  }, [addNotification, clubId]);

  const handleDeleteAdherent = useCallback(
    async (adherentId) => {
      if (demoMode) {
        addNotification(
          "Mode Démo : suppression désactivée.",
          "warning",
        );

        return;
      }

      const confirmed = window.confirm(
        "Êtes-vous sûr de vouloir supprimer cet adhérent ?",
      );

      if (!confirmed) return;

      setSubmitting(true);

      try {
        await deleteAdherent(adherentId);

        addNotification(
          "Adhérent supprimé avec succès.",
          "success",
        );

        await refreshAdherents();
      } catch (error) {
        console.error(
          "Erreur lors de la suppression de l'adhérent :",
          error,
        );

        addNotification(
          "Erreur lors de la suppression de l'adhérent.",
          "error",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      addNotification,
      demoMode,
      refreshAdherents,
    ],
  );

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  return {
    userInfo,
    userId,
    roleId,
    demoMode,

    club,
    clubId,
    adherents,
    dojos,
    groups,

    loading,
    submitting,

    refreshAdherents,
    reloadPage: loadPageData,
    handleDeleteAdherent,
  };
}